import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import TemplatesToolbar from "../../components/toolbar/TemplatesToolbar";
import { lockToast } from "../../helpers/successToast";
import { QuestionDescriptionDTO } from "../../types/Rows";
import { EditableSectionsList } from "./Questions/sectionsListEdit";
import { TemplateContextProvider, byWeight, useTemplateContext } from "./templateContext";
import AddItemModal from "./addItemModal";
import { useAddTemplateSection } from "../../hooks/useAddTemplateSection";
import { Textarea } from "../../components/ui/textarea";
import {
  assignQuestionToSection,
  fetchTemplate,
  fetchTemplateQuestions,
} from "../../api/templatesApi";
import { fetchAllDescriptions } from "../../api/reportsApi";
import { Skeleton } from "../../components/ui/Skeleton";
import { SectionsSkeleton } from "../report/ReportSkeleton";

function TemplatePage() {
  let { templateId } = useParams();
  const queryClient = useQueryClient();
  const [isLocked, setIsLocked] = useState<boolean>(true);
  const { sectionWeights } = useTemplateContext();
  const addSection = useAddTemplateSection(templateId!);

  const { data, isLoading, error } = useQuery(
    ["Templates", templateId] as unknown as QueryKey,
    async () => {
      let res = await fetchTemplate(templateId!);
      return res;
    },
    { enabled: templateId != undefined, refetchOnMount: false }
  );

  const [footer, setFooter] = useState<string>(data?.footer);
  const [footerFul, setFooterFul] = useState<string>(data?.footerFul);

  const { data: templateQuestions } = useQuery(
    ["Templates", templateId, "Questions"],
    async () => {
      let res = await fetchTemplateQuestions(templateId!);
      return res;
    },
    { enabled: templateId != undefined }
  );

  const setItems = useNavigationBadgesStore((state: any) => state.setItems);
  const footersFor = useRef<string | null>(null);

  useEffect(() => {
    if (data) {
      setItems([
        { text: "Badania", url: "/" } as NavigationBadge,
        {
          text: data.studyName,
          url: "/study/" + data.studyId,
        } as NavigationBadge,

        {
          text: "Szablon - " + data.name,
          url: "/templates/" + data.id,
        } as NavigationBadge,
      ]);
      // Stopki tylko przy pierwszym wczytaniu szablonu: odświeżenie danych nie kasuje niezapisanej treści
      if (footersFor.current !== data.id) {
        footersFor.current = data.id;
        setFooter(data.footer);
        setFooterFul(data.footerFul);
      }
      setIsLocked(data.isLocked);
    }
  }, [data, setItems]);

  const { data: allQuestions } = useQuery(
    ["Descriptions"],
    async () => await fetchAllDescriptions()
  );

  const [filteredAvailableQuestions, setFilteredAvailableQuestions] = useState<
    QuestionDescriptionDTO[]
  >([]);

  useEffect(() => {
    if (allQuestions && data) {
      setFilteredAvailableQuestions(
        allQuestions.filter(
          (x) =>
            !templateQuestions
              ?.flatMap((sections) => sections.questions ?? [])
              .flatMap((q) => q.id ?? [])
              .includes(x.id)
        )
      );
    }
  }, [allQuestions, data, templateQuestions]);

  const { mutate: saveQuestion } = useMutation(
    ({
      templateId,
      sectionId,
      descriptionId,
    }: {
      templateId: string;
      sectionId: string;
      descriptionId: string;
    }) => assignQuestionToSection(templateId, sectionId, descriptionId),
    {
      onSuccess: () => {
        queryClient.resetQueries(["Templates", templateId, "Questions"]);
      },
    }
  );

  // Tylko pierwsze wczytanie; odświeżenia (dodanie pytania, sekcji) nie przeładowują edytora
  if (error && !data) return <p className="p-8 text-[13px] text-[var(--sc-muted)]">Nie udało się wczytać szablonu.</p>;
  if (isLoading || !data) {
    return (
      <div className="flex h-[calc(100vh-56px)] bg-[var(--sc-bg)]">
        <aside className="w-[240px] flex-shrink-0 bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--sc-border)]">
            <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--sc-muted)]">Sekcje szablonu</p>
          </div>
          <div className="py-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-2 px-5 py-2">
                <span className="w-[18px] h-[18px] rounded-[4px] bg-[var(--sc-border)] flex-shrink-0" />
                <Skeleton className="h-3 flex-1" />
              </div>
            ))}
          </div>
        </aside>
        <main className="flex-1 overflow-y-auto px-8 py-5">
          <Skeleton className="h-6 w-56 mb-5" />
          <SectionsSkeleton />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-56px)] bg-[var(--sc-bg)]">
      {/* Sidebar */}
      <aside className="w-[240px] flex-shrink-0 bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--sc-border)]">
          <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--sc-muted)]">Sekcje szablonu</p>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {[...(templateQuestions ?? [])]
            .sort(byWeight(sectionWeights))
            .map((sec, i) => (
              <a
                key={sec.id}
                href={`#section-${sec.id}`}
                className="flex items-center gap-2 px-5 py-2 text-[12px] text-[var(--sc-muted)] hover:bg-[var(--sc-bg)] transition-colors no-underline"
              >
                <span className="w-[18px] h-[18px] rounded-[4px] bg-[var(--sc-border)] text-[10px] font-semibold text-[var(--sc-muted)] flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="flex-1 truncate">{sec.name}</span>
                <span className="text-[10px] text-[var(--sc-muted)]">{sec.questions?.length ?? 0}</span>
              </a>
            ))}
        </div>
        {!isLocked && (
          <div className="px-3 py-2 border-t border-[var(--sc-border)]">
            <AddItemModal text="Dodaj sekcję" borderless saveItem={(name) => addSection(name)}>
              <span className="text-sm">+</span> Dodaj sekcję
            </AddItemModal>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <TemplatesToolbar
          name={data.name}
          templateId={templateId}
          isLocked={isLocked}
          author={data.createdBy}
          footer={footer}
          footerFul={footerFul}
          templateType={data.templateType}
          studyId={data.studyId}
          studyName={data.studyName}
        />
        <main className="flex-1 overflow-y-auto px-8 py-5">
          <EditableSectionsList
            sections={templateQuestions ?? []}
            templateId={templateId!}
            isLocked={isLocked}
            existingItems={filteredAvailableQuestions}
            saveExisting={(sectionId, descriptionId) => {
              if (isLocked) { lockToast(); return; }
              return saveQuestion({ templateId: templateId!, sectionId, descriptionId });
            }}
          />

          <div className="mt-3 mb-5">
            <Textarea
              placeholder="Stopka"
              className="border w-full"
              style={{ height: "80px" }}
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
            />
            <Textarea
              placeholder="Stopka FUL"
              className="border w-full mt-2"
              style={{ height: "80px" }}
              value={footerFul}
              onChange={(e) => setFooterFul(e.target.value)}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

export const TemplatePageWrapper = () => {
  return (
    <TemplateContextProvider>
      <TemplatePage />
    </TemplateContextProvider>
  );
};
